<script lang="ts" setup>
import { EngagementSurveyTypeEnum } from '@/constants/survey';
import { resetProcedureRelatedState } from '@/services/userService';
import { useQuasar } from 'quasar';
import { getMagicLinkPatientProfileData, getPatientProfileData } from 'src/api/endpoints/userWeb';
import { handleApiRequest } from 'src/api/handleApiRequest';
import { loadPatientInfoAndTasks } from 'src/pages/TheDataHydrator';
import { useMessagesStore, useSessionStore } from 'src/store';
import { useDashboardStore } from 'src/store/dashboardModule';
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { PIQSetupPages } from 'src/services/piqSetupService';
import { HackedProcedures } from '@/constants/hacks';
import { InterruptType } from '@/constants/enums';
import {useAiSummariesStore} from "@/store/aiSummariesStore.ts";

const dashboardStore = useDashboardStore();
const $q = useQuasar()
const router = useRouter()

const sessionStore = useSessionStore();
const messagesStore = useMessagesStore();
const aiSummariesStore = useAiSummariesStore();
const isMagicLinkUser = computed(() => sessionStore.isMagicLinkUser);

const props = defineProps({
  reload: {
    type: String,
    default: 'true'
  }
})

onMounted(async () => {
  //Force redirect to change password
  if (sessionStore.requiresPasswordChange)
    router.push({ name: 'Settings.ChangePassword', params: { forcePasswordChange: 'true' } });

  if (props.reload === 'true') {
    // When procedure is changed we reset procedure data
    resetProcedureRelatedState()
  } else {
    // Reload Interrupts for later usages
    const patientResponse = await handleApiRequest(() => isMagicLinkUser.value ? getMagicLinkPatientProfileData() : getPatientProfileData(true), null, $q);

    if (patientResponse) {
      dashboardStore.loadPatient(patientResponse.result);
    }
  }

  $q.loading.show()

  try {
    await loadPatientInfoAndTasks(sessionStore, messagesStore, dashboardStore, aiSummariesStore, $q);

    $q.loading.hide()

    // some() can't be used with async/await directly, so we need to use a for loop
    var hasInterruptRedirect = false;
    for (const interrupt of dashboardStore.patient?.interruptData?.interrupts ?? []) {
      if (interrupt.type === InterruptType.CareTeam_Data_Share_Consent) {
        router.replace({ name: 'AboutYourData', params: { reload: props.reload } });
        hasInterruptRedirect = true;
        break;
      } else if (interrupt.type === InterruptType.Pain_Check_In) {
        router.replace({ name: 'PainRating', params: { reload: props.reload } });
        hasInterruptRedirect = true;
        break;
      } else if (interrupt.type === EngagementSurveyTypeEnum.day42 || interrupt.type === EngagementSurveyTypeEnum.day42NoPredictions) {
        router.replace({ name: 'Engagement', params: { surveyId: interrupt.surveyId } });
        hasInterruptRedirect = true;
        break;
      } else if (interrupt.type === InterruptType.Communication_Preference && !dashboardStore.skipCommunicationPreferencesInterrupt) {
        router.replace({ name: 'AboutYourCommunicationPreferences' });
        hasInterruptRedirect = true;
        break;
      } else if (interrupt.type === InterruptType.Hbs_Segment_One) {
        router.replace({ name: `PIQSetup.${PIQSetupPages.Start}` });
        hasInterruptRedirect = true;
        break;
      } else if (interrupt.type === InterruptType.Add_Procedure_Date) {
        router.replace({ name: 'Waitlist' });
        hasInterruptRedirect = true;
        break;
      } else if (interrupt.type === InterruptType.InputFieldSurvey) {
        router.replace({ name: 'Survey', params: { surveyId: interrupt.surveyId } });
        hasInterruptRedirect = true;
        break;
      }
    }

    //on procedure selection, all traffic is routed through interrupts first
    //so take hacked procedures into account when choosing where to route to next
    if (sessionStore.user?.selectedEocId === HackedProcedures.ImageViewer) {
      router.replace({ name: 'Images.MedStrat' });
    }
    else if (isMagicLinkUser && sessionStore.magicLinkEocId)
    {
      sessionStore.setMagicLinkEocId(null); // Clear magic link EOC ID after use
      router.replace({ name: 'MagicLinkIntro' });
    }
    else if (!hasInterruptRedirect) {
      var routeName = isMagicLinkUser.value ? 'Dashboard.MLHome' : 'Dashboard.Home';
      router.replace({ name: routeName, params: { reload: props.reload } });
    }
  } catch (error) {
    $q.loading.hide()
  }
})
</script>

<template>
  <div></div>
</template>